import { createSelector } from 'reselect'

import {
  centsToCurrency,
  formatCurrency,
  formatCurrencyFromCents,
} from '@/utils/currencyHelpers'
import type { ReduxState } from '@/utils/typeHelpers'
import { MONTHS_PER_YEAR } from './payrollPrice.constants'
import type { PayrollPriceAmounts } from './payrollPrice.types'

export interface PayrollPrices {
  base: string | null
  seat: string | null
  /** Base plus one person. */
  monthly: string | null
  /** Twelve months of base plus one person. */
  yearly: string | null
}

const WHOLE_DOLLARS = { hideDecimalsIfZero: true }

const formatCents = (cents: number | null) =>
  cents === null ? null : formatCurrencyFromCents(cents, WHOLE_DOLLARS)

const UNLOADED_PAYROLL_PRICES = {
  baseCents: null,
  seatCents: null,
} satisfies PayrollPriceAmounts

const buildPayrollPrices = ({
  baseCents,
  seatCents,
}: PayrollPriceAmounts): PayrollPrices => {
  const monthlyCurrency =
    baseCents === null || seatCents === null
      ? null
      : centsToCurrency(baseCents).add(centsToCurrency(seatCents))

  return {
    base: formatCents(baseCents),
    seat: formatCents(seatCents),
    monthly: monthlyCurrency && formatCurrency(monthlyCurrency, WHOLE_DOLLARS),
    yearly:
      monthlyCurrency &&
      formatCurrency(monthlyCurrency.multiply(MONTHS_PER_YEAR), WHOLE_DOLLARS),
  }
}

export const selectPayrollPrices = createSelector(
  (state: ReduxState) => state.payrollPrice,
  (loaded) => buildPayrollPrices(loaded ?? UNLOADED_PAYROLL_PRICES)
)
